import { useEffect, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { useLogin } from "@pontimed/auth-client";
import { signInPasskey } from "@pontimed/auth-shared/webauthn";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Fingerprint,
  Loader2,
  Mail,
  Phone,
  ShieldCheck,
} from "lucide-react";

import { useFirebasePhoneAuth } from "@/lib/firebase/use-firebase-phone-auth";
import { findCountryPhone } from "@/lib/phone-prefixes";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";

interface LoginSearch {
  next?: string;
}

const DEFAULT_RETURN = "/dashboard";
const OTP_TTL_SECONDS = 15 * 60;

/**
 * `next` comes from the URL, so anyone can craft it. Only a path on this
 * origin may reach window.location.assign or the API's return URL — the
 * portal's /auth/verify applies the same rule.
 */
function safeReturnPath(next: string | undefined): string {
  if (!next || !next.startsWith("/") || typeof window === "undefined") return DEFAULT_RETURN;
  try {
    const url = new URL(next, window.location.origin);
    if (url.origin !== window.location.origin) return DEFAULT_RETURN;
    return url.pathname + url.search + url.hash;
  } catch {
    return DEFAULT_RETURN;
  }
}

export const Route = createFileRoute("/_public/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  component: LoginPage,
});

type OtpMethod = "email" | "phone";

function LoginPage() {
  const { next } = Route.useSearch();
  const returnTo = safeReturnPath(next);
  const [otpMethod, setOtpMethod] = useState<OtpMethod>("email");

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col items-center justify-center gap-6 px-4 py-10">
      <header className="space-y-2 text-center">
        <div className="bg-primary/10 text-primary mx-auto flex size-10 items-center justify-center rounded-full">
          <ShieldCheck className="size-5" />
        </div>
        <h1 className="text-foreground text-2xl font-bold tracking-tight">
          <Trans>PontiMed Admin</Trans>
        </h1>
        <p className="text-muted-foreground text-sm">
          <Trans>Staff sign-in. Access restricted by IP allowlist + role check.</Trans>
        </p>
      </header>

      <Card className="w-full">
        <CardContent className="flex flex-col gap-4">
          <PasskeyPanel returnTo={returnTo} />
          <Divider />
          <GooglePanel returnTo={returnTo} />
          <Divider />
          <OtpMethodToggle method={otpMethod} onChange={setOtpMethod} />
          {otpMethod === "email" ? (
            <EmailPanel returnTo={returnTo} />
          ) : (
            <PhonePanel returnTo={returnTo} />
          )}
        </CardContent>
      </Card>

      <div id="recaptcha-container" />

      <p className="text-muted-foreground max-w-sm text-center text-xs">
        <Trans>
          Unauthorized access is logged and reviewed. By continuing, you agree to PontiMed's staff
          acceptable use policy.
        </Trans>
      </p>
    </main>
  );
}

function PasskeyPanel({ returnTo }: { returnTo: string }) {
  const { t } = useLingui();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.PublicKeyCredential) {
      setSupported(false);
      return;
    }
    PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
      .then(setSupported)
      .catch(() => setSupported(false));
  }, []);

  const onClick = async () => {
    setError(null);
    setLoading(true);
    try {
      const result = await signInPasskey();
      if (result.ok) {
        // Passkey + UV=required also mints pm_2fa server-side, so the user
        // skips the TOTP challenge. Hard-navigate so the auth-client cache
        // re-bootstraps with the fresh cookies.
        if (typeof window !== "undefined") window.location.assign(returnTo);
        return;
      }
      if (result.error === "cancelled") {
        // Silent — user dismissed the OS prompt.
        return;
      }
      if (result.error === "no_credential") {
        setError(
          t`No passkey found for this device. Sign in another way first, then enable Sign-in from your portal Settings.`,
        );
      } else if (result.error === "rate_limited") {
        setError(t`Too many attempts. Try again later.`);
      } else {
        setError(t`Could not sign in with this passkey. Try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Hide the panel on devices that flat-out can't surface a platform
  // authenticator — there's nothing actionable to show.
  if (supported === false) return null;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">
        <Trans>Use Face ID, Touch ID or Windows Hello if you've enabled passkey sign-in.</Trans>
      </p>
      <Button
        variant="default"
        className="h-11 w-full gap-2"
        onClick={onClick}
        disabled={loading || supported === null}
      >
        {loading ? <Loader2 className="size-4 animate-spin" /> : <Fingerprint className="size-4" />}
        <Trans>Sign in with passkey</Trans>
      </Button>
      {error && (
        <p className="text-destructive flex items-start gap-1.5 text-xs">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

function GooglePanel({ returnTo }: { returnTo: string }) {
  const login = useLogin();
  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-xs">
        <Trans>Use your PontiMed Google Workspace account.</Trans>
      </p>
      <Button
        variant="outline"
        className="h-11 w-full"
        onClick={() => login.loginWithOAuth("google", returnTo)}
      >
        <Trans>Continue with Google</Trans>
      </Button>
    </div>
  );
}

function Divider() {
  return (
    <div className="border-border flex items-center gap-2 border-t pt-3">
      <span className="text-muted-foreground text-[10px] tracking-wide uppercase">
        <Trans>Or</Trans>
      </span>
    </div>
  );
}

function OtpMethodToggle({
  method,
  onChange,
}: {
  method: OtpMethod;
  onChange: (m: OtpMethod) => void;
}) {
  return (
    <div className="bg-muted/50 relative flex rounded-lg p-1">
      <div
        className="bg-background absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-md shadow-sm transition-transform duration-200 ease-out"
        style={{
          transform: method === "phone" ? "translateX(calc(100% + 4px))" : "translateX(0)",
        }}
      />
      <button
        type="button"
        onClick={() => onChange("email")}
        aria-pressed={method === "email"}
        className={`text-foreground relative z-10 flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
          method === "email" ? "" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        <Mail className="size-3.5" />
        <Trans>Email</Trans>
      </button>
      <button
        type="button"
        onClick={() => onChange("phone")}
        aria-pressed={method === "phone"}
        className={`text-foreground relative z-10 flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
          method === "phone" ? "" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        <Phone className="size-3.5" />
        <Trans>Phone</Trans>
      </button>
    </div>
  );
}

// ─── Email — OTP code (legacy parity, NOT magic link) ──────────────────────

function EmailPanel({ returnTo }: { returnTo: string }) {
  const { t } = useLingui();
  const [step, setStep] = useState<"enter-email" | "enter-code">("enter-email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [serverError, setServerError] = useState<unknown>(null);
  const countdown = useCountdown(step === "enter-code" ? OTP_TTL_SECONDS : 0);

  const sendMutation = useMutation({
    mutationFn: async () => {
      setServerError(null);
      await apiFetch.post(
        "/api/auth/send-email-otp",
        {
          email,
          locale: navigator.language?.slice(0, 2) || "ro",
          // The mail's magic link opens on the portal; this is where it should
          // send the user afterwards (absolute — validated by the API).
          next: new URL(returnTo, window.location.origin).href,
        },
        { skipAuthRefresh: true },
      );
    },
    onSuccess: () => setStep("enter-code"),
    onError: (e) => setServerError(e),
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      setServerError(null);
      await apiFetch.post(
        "/api/auth/verify-email-otp",
        { email, otp: code },
        { skipAuthRefresh: true },
      );
    },
    onSuccess: () => {
      if (typeof window !== "undefined") window.location.assign(returnTo);
    },
    onError: (e) => setServerError(e),
  });

  const sendRetryAfter = useRetryAfterCountdown(serverError);

  if (step === "enter-email") {
    return (
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!email || sendMutation.isPending || sendRetryAfter) return;
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
          sendMutation.mutate();
        }}
      >
        <p className="text-muted-foreground text-xs">
          <Trans>We'll email a 6-digit code to your work address.</Trans>
        </p>
        <Input
          type="email"
          required
          autoComplete="email"
          autoFocus
          placeholder={t`you@pontimed.app`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11"
          disabled={sendMutation.isPending}
        />
        <Button
          type="submit"
          className="h-11 gap-1.5"
          disabled={
            sendMutation.isPending ||
            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ||
            sendRetryAfter !== null
          }
        >
          {sendMutation.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Mail className="size-4" />
          )}
          {sendRetryAfter !== null ? (
            <Trans>Try again in {sendRetryAfter}s</Trans>
          ) : (
            <Trans>Send code</Trans>
          )}
        </Button>
        <ErrorLine error={serverError} retryAfter={sendRetryAfter} />
      </form>
    );
  }

  return (
    <OtpCodeForm
      destination={email}
      code={code}
      setCode={setCode}
      countdown={countdown}
      pending={verifyMutation.isPending}
      error={serverError}
      onSubmit={() => verifyMutation.mutate()}
      onResend={() => {
        setCode("");
        setServerError(null);
        sendMutation.mutate();
      }}
      onBack={() => {
        setStep("enter-email");
        setCode("");
        setServerError(null);
      }}
    />
  );
}

// ─── Phone — Firebase reCAPTCHA + apps/api JWT mint ────────────────────────

function PhonePanel({ returnTo }: { returnTo: string }) {
  const { requestPhoneOtp, verifyPhoneOtp } = useLogin();
  const firebasePhone = useFirebasePhoneAuth();
  const [step, setStep] = useState<"enter-phone" | "enter-code">("enter-phone");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("ro");
  const [code, setCode] = useState("");
  const [serverError, setServerError] = useState<unknown>(null);
  const countdown = useCountdown(step === "enter-code" ? OTP_TTL_SECONDS : 0);

  const phonePrefix = findCountryPhone(countryCode).prefix;
  const fullPhone = `${phonePrefix}${phone.replace(/\s+/g, "")}`;

  const sendMutation = useMutation({
    mutationFn: async () => {
      setServerError(null);
      await requestPhoneOtp(fullPhone);
      const ok = await firebasePhone.sendOtp(fullPhone, "recaptcha-container");
      if (!ok) throw new Error(firebasePhone.error || "Failed to send SMS");
    },
    onSuccess: () => setStep("enter-code"),
    onError: (e) => setServerError(e),
  });

  const verifyMutation = useMutation({
    mutationFn: async () => {
      setServerError(null);
      const result = await firebasePhone.verifyOtp(code);
      if (!result?.idToken) throw new Error(firebasePhone.error || "Invalid code");
      await verifyPhoneOtp(fullPhone, result.idToken);
    },
    onSuccess: () => {
      firebasePhone.cleanup();
      if (typeof window !== "undefined") window.location.assign(returnTo);
    },
    onError: (e) => setServerError(e),
  });

  const sendRetryAfter = useRetryAfterCountdown(serverError);

  if (step === "enter-phone") {
    return (
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (sendMutation.isPending || phone.length < 6) return;
          sendMutation.mutate();
        }}
      >
        <p className="text-muted-foreground text-xs">
          <Trans>Pick your country code, type your number, and we'll SMS you a 6-digit code.</Trans>
        </p>
        <PhoneInput
          value={phone}
          onChange={setPhone}
          countryCode={countryCode}
          onCountryChange={(code) => setCountryCode(code)}
          enterKeyHint="send"
          disabled={sendMutation.isPending}
          autoFocus
        />
        <Button
          type="submit"
          className="h-11 gap-1.5"
          disabled={
            sendMutation.isPending ||
            firebasePhone.loading ||
            phone.length < 6 ||
            sendRetryAfter !== null
          }
        >
          {sendMutation.isPending || firebasePhone.loading ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Phone className="size-4" />
          )}
          {sendRetryAfter !== null ? (
            <Trans>Try again in {sendRetryAfter}s</Trans>
          ) : (
            <Trans>Send code</Trans>
          )}
        </Button>
        <ErrorLine error={serverError} retryAfter={sendRetryAfter} />
        {firebasePhone.error && !sendMutation.isError && (
          <p className="text-destructive text-xs">{firebasePhone.error}</p>
        )}
      </form>
    );
  }

  return (
    <OtpCodeForm
      destination={fullPhone}
      code={code}
      setCode={setCode}
      countdown={countdown}
      pending={verifyMutation.isPending || firebasePhone.loading}
      error={serverError ?? (firebasePhone.error ? new Error(firebasePhone.error) : null)}
      onSubmit={() => verifyMutation.mutate()}
      onResend={() => {
        setCode("");
        setServerError(null);
        sendMutation.mutate();
      }}
      onBack={() => {
        setStep("enter-phone");
        setCode("");
        firebasePhone.cleanup();
        setServerError(null);
      }}
    />
  );
}

// ─── Shared OTP-code entry form ────────────────────────────────────────────

function OtpCodeForm({
  destination,
  code,
  setCode,
  countdown,
  pending,
  error,
  onSubmit,
  onResend,
  onBack,
}: {
  destination: string;
  code: string;
  setCode: (v: string) => void;
  countdown: number;
  pending: boolean;
  error: unknown;
  onSubmit: () => void;
  onResend: () => void;
  onBack: () => void;
}) {
  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;
  const timer = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (pending || code.length < 6) return;
        onSubmit();
      }}
    >
      <p className="text-muted-foreground text-xs">
        <Trans>Enter the 6-digit code we sent to {destination}.</Trans>
      </p>
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        placeholder="000000"
        maxLength={6}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
        disabled={pending}
        className="h-12 text-center text-2xl tracking-[0.5em]"
      />
      {countdown > 0 && (
        <p className="text-muted-foreground text-center text-xs">
          <Trans>Code expires in {timer}</Trans>
        </p>
      )}
      <Button type="submit" className="h-11 gap-1.5" disabled={pending || code.length < 6}>
        {pending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <CheckCircle2 className="size-4" />
        )}
        <Trans>Verify and sign in</Trans>
      </Button>
      <ErrorLine error={error} retryAfter={null} />
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          onClick={onBack}
          className="text-muted-foreground hover:text-foreground"
        >
          <Trans>← Back</Trans>
        </button>
        <button
          type="button"
          onClick={onResend}
          disabled={pending || countdown > OTP_TTL_SECONDS - 30}
          className="text-primary hover:underline disabled:opacity-50"
        >
          <Trans>Resend code</Trans>
        </button>
      </div>
    </form>
  );
}

function ErrorLine({ error, retryAfter }: { error: unknown; retryAfter: number | null }) {
  if (!error) return null;
  if (retryAfter !== null) {
    return (
      <p className="inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
        <AlertTriangle className="size-3.5" />
        <Trans>Too many attempts. Wait {retryAfter}s before trying again.</Trans>
      </p>
    );
  }
  if (error instanceof ApiError) {
    if (error.status === 400) {
      return (
        <p className="text-destructive text-xs">
          <Trans>Please check the format and try again.</Trans>
        </p>
      );
    }
    if (error.status === 401) {
      return (
        <p className="text-destructive text-xs">
          <Trans>Invalid or expired code.</Trans>
        </p>
      );
    }
    return (
      <p className="text-destructive text-xs">
        <Trans>Could not complete. Try again.</Trans>
      </p>
    );
  }
  if (error instanceof Error) {
    return <p className="text-destructive text-xs">{error.message}</p>;
  }
  return null;
}

function useRetryAfterCountdown(error: unknown): number | null {
  const initial =
    error instanceof ApiError &&
    error.status === 429 &&
    typeof error.body === "object" &&
    error.body !== null &&
    "retryAfter" in error.body
      ? Number((error.body as { retryAfter?: unknown }).retryAfter)
      : null;

  const [seconds, setSeconds] = useState<number | null>(
    initial && Number.isFinite(initial) ? initial : null,
  );

  useEffect(() => {
    if (initial && Number.isFinite(initial)) setSeconds(initial);
    else setSeconds(null);
  }, [initial]);

  useEffect(() => {
    if (seconds === null || seconds <= 0) return;
    const id = setInterval(() => {
      setSeconds((prev) => (prev === null || prev <= 1 ? null : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [seconds]);

  return seconds && seconds > 0 ? seconds : null;
}

function useCountdown(initialSeconds: number): number {
  const [seconds, setSeconds] = useState(initialSeconds);
  const startedAt = useRef(initialSeconds);

  useEffect(() => {
    if (initialSeconds !== startedAt.current) {
      startedAt.current = initialSeconds;
      setSeconds(initialSeconds);
    }
  }, [initialSeconds]);

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setInterval(() => {
      setSeconds((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [seconds]);

  return seconds;
}
